import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

export default function CartPage() {
  const { cart, updateQuantity, removeFromCart, loading } = useCart();
  const { isAuthenticated } = useAuth();
  const { t, tField } = useLang();
  const navigate = useNavigate();
  const { toast } = useToast();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center py-16">
          <ShoppingBag className="w-16 h-16 mx-auto text-[#E5E0D8] mb-6" />
          <h1 className="text-3xl font-light mb-4">{t({ it: 'Il tuo carrello', en: 'Your cart' })}</h1>
          <p className="text-[#57534E] mb-8">
            {t({ it: 'Accedi per visualizzare il tuo carrello', en: 'Sign in to view your cart' })}
          </p>
          <Link
            to="/login"
            data-testid="cart-login-btn"
            className="btn-primary inline-flex items-center gap-2"
          >
            {t({ it: 'Accedi', en: 'Sign In' })}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-light mb-8">Il tuo Carrello</h1>
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-6 animate-pulse">
                <div className="w-24 h-32 bg-[#E5E0D8]" />
                <div className="flex-1 space-y-3">
                  <div className="h-4 bg-[#E5E0D8] w-1/2" />
                  <div className="h-4 bg-[#E5E0D8] w-1/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center py-16">
          <ShoppingBag className="w-16 h-16 mx-auto text-[#E5E0D8] mb-6" />
          <h1 className="text-3xl font-light mb-4">{t({ it: 'Il tuo carrello è vuoto', en: 'Your cart is empty' })}</h1>
          <p className="text-[#57534E] mb-8">
            {t({ it: 'Esplora la nostra collezione e aggiungi i tuoi preferiti', en: 'Explore our collection and add your favourites' })}
          </p>
          <Link
            to="/shop"
            data-testid="continue-shopping-btn"
            className="btn-primary inline-flex items-center gap-2"
          >
            {t({ it: 'Continua lo Shopping', en: 'Continue Shopping' })}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    );
  }

  const handleQuantityChange = async (item, newQuantity) => {
    if (newQuantity < 1) {
      await removeFromCart(item.product_id, item.size, item.color);
      toast({ title: 'Prodotto rimosso dal carrello' });
    } else {
      await updateQuantity({
        product_id: item.product_id,
        quantity: newQuantity,
        size: item.size,
        color: item.color,
      });
    }
  };

  const handleRemove = async (item) => {
    await removeFromCart(item.product_id, item.size, item.color);
    toast({ title: 'Prodotto rimosso dal carrello' });
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl md:text-4xl font-light tracking-tight mb-8">
          {t({ it: 'Il tuo Carrello', en: 'Your Cart' })}
        </h1>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {cart.items.map((item, idx) => (
              <div
                key={`${item.product_id}-${item.size}-${item.color}`}
                data-testid={`cart-item-${idx}`}
                className="flex gap-6 p-6 bg-[#F2EFE9]"
              >
                <Link to={`/product/${item.product_id}`} className="shrink-0">
                  <img
                    src={item.product?.images?.[0] || 'https://via.placeholder.com/120x160'}
                    alt={item.product?.name}
                    className="w-24 h-32 object-cover"
                  />
                </Link>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <div>
                      <Link
                        to={`/product/${item.product_id}`}
                        className="font-medium hover:text-[#4A5D23] transition-colors"
                      >
                        {tField(item.product, 'name')}
                      </Link>
                      <p className="text-sm text-[#57534E] mt-1">
                        {item.size} / {item.color}
                      </p>
                    </div>
                    <p className="font-medium">
                      €{item.item_total?.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleQuantityChange(item, item.quantity - 1)}
                        data-testid={`cart-decrease-${idx}`}
                        className="w-8 h-8 flex items-center justify-center border border-[#E5E0D8] hover:border-[#1C1917] transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() => handleQuantityChange(item, item.quantity + 1)}
                        data-testid={`cart-increase-${idx}`}
                        className="w-8 h-8 flex items-center justify-center border border-[#E5E0D8] hover:border-[#1C1917] transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      onClick={() => handleRemove(item)}
                      data-testid={`cart-remove-${idx}`}
                      className="text-[#C05746] hover:text-[#1C1917] transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-[#F2EFE9] p-6 sticky top-24">
              <h2 className="text-xl font-medium mb-6">{t({ it: 'Riepilogo Ordine', en: 'Order Summary' })}</h2>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-[#57534E]">
                  <span>{t({ it: 'Subtotale', en: 'Subtotal' })}</span>
                  <span>€{cart.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#57534E]">
                  <span>{t({ it: 'Spedizione', en: 'Shipping' })}</span>
                  <span>{cart.total >= 300 ? t({ it: 'Gratuita', en: 'Free' }) : '€6.90'}</span>
                </div>
                <div className="border-t border-[#E5E0D8] pt-4">
                  <div className="flex justify-between text-lg font-medium">
                    <span>{t({ it: 'Totale', en: 'Total' })}</span>
                    <span>€{(cart.total + (cart.total >= 300 ? 0 : 6.90)).toFixed(2)}</span>
                  </div>
                </div>
                <p className="text-xs text-[#57534E]">
                  {t({
                    it: 'Eventuali sconti Biolosophy Value approvati verranno applicati al checkout.',
                    en: 'Any approved Biolosophy Value discounts will be applied at checkout.',
                  })}
                </p>
              </div>
              <button
                onClick={() => navigate('/checkout')}
                data-testid="proceed-to-checkout-btn"
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                {t({ it: 'Procedi al Checkout', en: 'Proceed to Checkout' })}
                <ArrowRight className="w-5 h-5" />
              </button>
              <Link
                to="/shop"
                className="block text-center mt-4 text-[#57534E] hover:text-[#1C1917] transition-colors"
              >
                {t({ it: 'Continua lo Shopping', en: 'Continue Shopping' })}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
